import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { cpApi, type CpSmsProfileEvent, type CpSmsProfileStatus } from '../api/api';

export default function VndQrProfileSmsBlock({
  profileId,
  bankName,
}: {
  profileId: number | null;
  bankName?: string;
}) {
  const { t } = useTranslation();
  const [st, setSt] = useState<CpSmsProfileStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [events, setEvents] = useState<CpSmsProfileEvent[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [eventsBusy, setEventsBusy] = useState(false);
  const limit = 10;

  const load = useCallback(async () => {
    if (!profileId) return;
    try {
      setSt(await cpApi.getVndQrProfileSms(profileId));
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('qrForm.smsLoadError'));
    }
  }, [profileId, t]);

  const loadEvents = useCallback(async () => {
    if (!profileId) return;
    setEventsBusy(true);
    try {
      const res = await cpApi.getVndQrProfileSmsEvents(profileId, { page, limit });
      setEvents(res.events ?? []);
      setTotal(Number(res.total ?? 0));
      setTotalPages(Math.max(1, Number(res.total_pages ?? 1)));
    } catch {
      setEvents([]);
    } finally {
      setEventsBusy(false);
    }
  }, [profileId, page]);

  useEffect(() => {
    void load();
    if (!profileId) return;
    const timer = window.setInterval(() => void load(), 8000);
    return () => window.clearInterval(timer);
  }, [load, profileId]);

  useEffect(() => {
    setPage(1);
  }, [profileId]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  if (!profileId) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
        {t('qrForm.smsSaveFirst')}
      </div>
    );
  }

  const listenMode: 'sms' | 'notification' =
    st?.listen_mode === 'sms' || st?.listen_mode === 'notification'
      ? st.listen_mode
      : (bankName ?? '').toUpperCase().includes('BIDV') || (bankName ?? '').toUpperCase().includes('VCB')
        ? 'notification'
        : 'sms';

  async function setEnabled(enabled: boolean) {
    if (!profileId) return;
    setBusy(true);
    try {
      setSt(await cpApi.putVndQrProfileSms(profileId, { enabled, listen_mode: listenMode }));
      setErr(null);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const d = e.response?.data as { error?: string; message?: string } | string | undefined;
        const fromApi = typeof d === 'string' ? d.trim() : (d?.message || d?.error || '').trim();
        setErr(fromApi || t('qrForm.smsSaveError'));
      } else {
        setErr(e instanceof Error ? e.message : t('qrForm.smsSaveError'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{t('qrForm.smsTitle')}</div>
          <p className="text-[11px] leading-snug text-slate-500 dark:text-slate-400">
            {st?.enabled ? t('qrForm.smsEnabledHint') : t('qrForm.smsDisabledHint')}
          </p>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => void setEnabled(!(st?.enabled ?? false))}
          className={`relative inline-flex h-7 w-11 shrink-0 items-center rounded-full ${
            st?.enabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
          } disabled:opacity-50`}
          aria-pressed={!!st?.enabled}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ${
              st?.enabled ? 'translate-x-[22px]' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      {err ? <p className="mt-2 text-[12px] text-rose-700 dark:text-rose-300">{err}</p> : null}
      {st?.phone?.label ? (
        <p className="mt-2 text-[12px] text-slate-600 dark:text-slate-300">
          {t('qrForm.smsPhone')}: <span className="font-medium">{st.phone.label}</span>
          <Link to="/sms-phones" className="ml-2 font-semibold text-violet-700 hover:underline dark:text-violet-300">
            {t('qrForm.smsManagePhones')}
          </Link>
        </p>
      ) : (
        <p className="mt-2 text-[12px] text-amber-800 dark:text-amber-200">
          {t('qrForm.smsNoPhone')}{' '}
          <Link to="/sms-phones" className="font-semibold text-violet-700 hover:underline dark:text-violet-300">
            {t('qrForm.smsManagePhones')}
          </Link>
        </p>
      )}
      {st?.device && (st.device.locked === true || st.device.screen_on === false) ? (
        <p className="mt-2 text-[12px] font-semibold text-rose-700 dark:text-rose-300">
          {t('qrForm.smsDeviceWarn', {
            state: st.device.locked ? t('qrForm.smsLocked') : t('qrForm.smsScreenOff'),
          })}
        </p>
      ) : null}

      <div className="mt-3 rounded-lg border border-slate-100 dark:border-white/10">
        <div className="flex items-center justify-between border-b border-slate-100 px-2.5 py-1.5 dark:border-white/10">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {t('qrForm.smsEvents')}
          </span>
          <span className="text-[11px] text-slate-400">
            {eventsBusy ? t('common.loadingEllipsis') : t('qrForm.smsEventsCount', { total })}
          </span>
        </div>
        {events.length === 0 ? (
          <p className="px-2.5 py-3 text-[12px] text-slate-500 dark:text-slate-400">{t('qrForm.smsEventsEmpty')}</p>
        ) : (
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="sticky top-0 bg-slate-50 text-slate-500 dark:bg-white/[0.06] dark:text-slate-400">
                <tr>
                  <th className="px-2 py-1.5 font-medium">{t('qrForm.smsColWhen')}</th>
                  <th className="px-2 py-1.5 font-medium">{t('qrForm.smsColAmount')}</th>
                  <th className="px-2 py-1.5 font-medium">{t('qrForm.smsColStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id} className="border-t border-slate-100 text-slate-700 dark:border-white/10 dark:text-slate-200">
                    <td className="whitespace-nowrap px-2 py-1.5 text-slate-500 dark:text-slate-400">
                      {ev.created_at ? new Date(ev.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-2 py-1.5 font-mono">{ev.amount_vnd ? Number(ev.amount_vnd).toLocaleString() : '—'}</td>
                    <td className="px-2 py-1.5">{ev.confirm_status || ev.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-2.5 py-1.5 dark:border-white/10">
          <span className="text-[11px] text-slate-500">
            {t('qrForm.pageOf', { page, totalPages })}
          </span>
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={page <= 1 || eventsBusy}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700 disabled:opacity-40 dark:border-white/15 dark:bg-white/5 dark:text-slate-200"
            >
              {t('qrForm.prevPage')}
            </button>
            <button
              type="button"
              disabled={page >= totalPages || eventsBusy}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700 disabled:opacity-40 dark:border-white/15 dark:bg-white/5 dark:text-slate-200"
            >
              {t('qrForm.nextPage')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
